import {
  labAnalyteName,
  type LabReferenceBucket,
  type LabReviewOutcome,
} from "@care-by-ella/contracts";

// TES-201 — clinician-facing chrome for the lab-feedback surface. Swedish is the
// product-approved base (English is derived at the mobile i18n layer, not here —
// the admin is Swedish-only). The analyte names live in contracts since
// TES-1058 (LAB_ANALYTE_NAMES), so the journal note names a value the same way.

export function analyteName(analyteKey: string): string {
  return labAnalyteName(analyteKey);
}

// Bucket → patient-facing badge label. `in_range` is the "green zone"; `low`/
// `high` flag out-of-range; `unknown` is shown muted when no range applies.
export const BUCKET_LABEL: Record<LabReferenceBucket, string> = {
  in_range: "Inom mål",
  low: "Lågt",
  high: "Högt",
  unknown: "Okänt",
};

// Bucket → Tailwind classes for the badge chip (token colours, success = the
// #2E9E6A health green; warning for out-of-range so it reads as "look here").
export const BUCKET_BADGE_CLASS: Record<LabReferenceBucket, string> = {
  in_range: "bg-success/10 text-success border-success/30",
  low: "bg-warning/10 text-warning border-warning/40",
  high: "bg-warning/10 text-warning border-warning/40",
  unknown: "bg-surfaceMuted text-textSecondary border-surfaceMuted",
};

// Render a result's value + unit; falls back to the free-text value, then a dash.
export function formatValue(value: number | null, valueText: string | null) {
  if (value !== null) return String(value);
  if (valueText) return valueText;
  return "—";
}

// Render the female reference range as "x–y", "≤ y", or "≥ x".
export function formatRange(
  refLow: number | null,
  refHigh: number | null,
): string | null {
  if (refLow !== null && refHigh !== null) return `${refLow}–${refHigh}`;
  if (refHigh !== null) return `≤ ${refHigh}`;
  if (refLow !== null) return `≥ ${refLow}`;
  return null;
}

// Maps the feedback endpoints' machine `error` codes to Swedish doctor copy.
export function labFeedbackErrorMessage(code: string): string {
  switch (code) {
    case "lab_order_not_found":
      return "Labbordern kunde inte hittas.";
    case "lab_order_not_resulted":
      return "Provsvaren har inte kommit in än.";
    case "validation_failed":
      return "Feedbacken kunde inte sparas. Kontrollera fälten.";
    // TES-1370 (Ö-582)
    case "lab_feedback_not_approved":
      return "Godkänn sammanfattningen och varje kommentar till ett provvärde innan du publicerar.";
    case "lab_feedback_empty":
      return "Skriv en text innan du godkänner eller publicerar.";
    case "lab_feedback_already_sent":
      return "Provsvaret skickades före granskningsflödet och kan inte ändras här.";
    case "lab_feedback_unknown_analyte":
      return "Provvärdet finns inte i den här beställningen.";
    default:
      return "Något gick fel. Försök igen.";
  }
}

// TES-890 (Ö-185) — the three conclusions a doctor's review of a result can
// record. TES-952 made the review implicit in the treatment decision, so these
// are now only READ back (the order row and the case status), never chosen.
export const REVIEW_OUTCOME_LABEL: Record<LabReviewOutcome, string> = {
  approved: "Godkänt underlag",
  completion_required: "Komplettering krävs",
  new_sample_required: "Nytt prov krävs",
};
